import { useSelector } from 'react-redux';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { DRAFT_DEAL_LIMIT } from '../../../../constants';

export interface AISectionVisibilityParams {
    scopeId: string;
}

export interface AISectionVisibilityResult {
    // Affordability visibility
    showAffordabilityLoader: boolean;
    showAffordabilityCard: boolean;
    hasLenderProbabilities: boolean;
    /**
     * Aggregated approval likelihood derived from the same dealer-configured lender list
     * the Lender slide-out displays — "High" if any returned lender's `loanLikelihood === 'High'`,
     * otherwise "Limited". Use this for the "Lender Approval Likelihood" card so its label
     * cannot disagree with the per-lender labels shown in the slide-out.
     */
    lenderApprovalLevel: 'High' | 'Limited';
    predictedRate: string;

    // Recommendations visibility
    showRecommendationsLoader: boolean;
    showRecommendationsCard: boolean;
    isRecommendationsCTADisabled: boolean;
    isAiRecommendationsCTAAvailable: boolean;
    isAtMaxScenarios: boolean;

    // Alt Deal Structures visibility
    showAltDealStructuresCard: boolean;

    // Overall AI section visibility
    showAISection: boolean;
}

/**
 * Custom hook to manage AI section visibility logic in BaseDealContainer
 *
 * Extracts complex visibility boolean derivations for AI Affordability and
 * AI Vehicle Recommendations sections, making them easier to test and reuse.
 *
 * @param params - Object containing scopeId for scope-related visibility checks
 * @returns Object containing all visibility flags for AI section components
 */
export const useAISectionVisibility = ({ scopeId }: AISectionVisibilityParams): AISectionVisibilityResult => {
    // Feature toggles
    const isAIAffordabilityIndicatorEnabled = useSelector(featureToggleSelectors.isAIAffordabilityIndicatorEnabled);
    const isAIVehicleRecommendationEnabled = useSelector(featureToggleSelectors.isAIVehicleRecommendationEnabled);
    const isAIVehicleRecommendationEnabledForLease = useSelector(featureToggleSelectors.isAIVehicleRecommendationEnabledForLease);
    const isAiAltDealStructureRecommendationsEnabled = useSelector(
        featureToggleSelectors.isAiAltDealStructureRecommendationsEnabled
    );
    const isAiVehicleRecommendationsChangeVehicleEnabled = useSelector(
        featureToggleSelectors.isAiVehicleRecommendationsChangeVehicleEnabled
    );

    // Scope selectors
    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const isPrimaryScope = primaryScopeId === scopeId;
    const scopesOrder = useSelector(scopedStateSelectors.getScopesOrder);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);

    // Offer type
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isFinanceOffer = currentOfferType === FINANCE;
    const isLeaseOffer = currentOfferType === LEASE;

    // AI affordability selectors — sourced from lender-probability so the card matches the slide-out.
    // The aggregate level + APR are computed on the backend and surfaced at the response root.
    const isLenderProbabilitiesLoading = useSelector(deskingSelectors.getLenderProbabilitiesLoading);
    const lenderProbabilities = useSelector(deskingSelectors.getLenderProbabilities);
    const lenderApprovalLevel = useSelector(deskingSelectors.getLenderAffordabilityLevel);
    const predictedRate = useSelector(deskingSelectors.getLenderPredictedRate);

    // AI Vehicle Recommendations selectors
    const shouldShowRecommendationsCTA = useSelector(deskingSelectors.shouldShowAiRecommendationsCTA);
    const isAiRecommendationsCTAAvailable = useSelector(deskingSelectors.isAiRecommendationsCTAAvailable);

    // Affordability visibility logic (finance-only)
    const hasLenderProbabilities = !!(lenderProbabilities && lenderProbabilities.length > 0);
    const showAffordabilityLoader = isAIAffordabilityIndicatorEnabled && isFinanceOffer && isLenderProbabilitiesLoading;
    const showAffordabilityCard = isAIAffordabilityIndicatorEnabled && isFinanceOffer && !isLenderProbabilitiesLoading;

    // Alt Deal Structures visibility logic (finance-only, Deal Central primary scope only)
    const showAltDealStructuresCard =
        isAiAltDealStructureRecommendationsEnabled && isFinanceOffer && isDealCentralExperience && isPrimaryScope;

    // Recommendations visibility logic
    const isAtMaxScenarios = scopesOrder.length >= DRAFT_DEAL_LIMIT;
    const isRecommendationsSurfaceOnDashboard = isAIVehicleRecommendationEnabled && !isAiVehicleRecommendationsChangeVehicleEnabled;
    const showRecommendationsLoader =
        isRecommendationsSurfaceOnDashboard && (isPrimaryScope || !isDealCentralExperience) && !shouldShowRecommendationsCTA;
    const showRecommendationsCard =
        isRecommendationsSurfaceOnDashboard && (isPrimaryScope || !isDealCentralExperience) && shouldShowRecommendationsCTA;
    const isRecommendationsCTADisabled = !isAiRecommendationsCTAAvailable || isAtMaxScenarios;

    // Overall AI section visibility
    // Finance affordability or alt deal structures
    const showFinanceAffordabilityOrAltDealAI =
        isFinanceOffer && (showAffordabilityLoader || showAffordabilityCard || showAltDealStructuresCard);

    // Vehicle recommendations
    const shouldFetchVehicleRecommendationsForLease = isLeaseOffer && isAIVehicleRecommendationEnabledForLease;
    const showVehicleRecommendationsAI =
        (isFinanceOffer || shouldFetchVehicleRecommendationsForLease) && (showRecommendationsLoader || showRecommendationsCard);

    const showAISection = showFinanceAffordabilityOrAltDealAI || showVehicleRecommendationsAI;

    return {
        // Affordability visibility
        showAffordabilityLoader,
        showAffordabilityCard,
        hasLenderProbabilities,
        lenderApprovalLevel,
        predictedRate,

        // Recommendations visibility
        showRecommendationsLoader,
        showRecommendationsCard,
        isRecommendationsCTADisabled,
        isAiRecommendationsCTAAvailable,
        isAtMaxScenarios,

        // Alt Deal Structures visibility
        showAltDealStructuresCard,

        // Overall AI section visibility
        showAISection
    };
};
